import {
  useCallback,
  RefObject,
  KeyboardEventHandler,
  useRef,
  useMemo,
} from 'react';

import {
  useSelect,
  UseSelectState,
  UseSelectStateChange,
  UseSelectStateChangeOptions,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';

import { useForwardedRef } from '@adsk/alloy-react-helpers';

import useDropdown from './useDropdown';
import useDropdownId from './useDropdownId';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  createDefaultGetItemKey,
  deduplicateItems,
  includesItem,
  isGroupHeader,
} from '../helpers';
import { flattenGroupItemsToDownshift } from '../helpers/groupedItems';
import {
  getMultipleSelectionHandlers,
  handleMultiItemSelect,
} from '../helpers/sharedMultipleSelection';
import {
  DefaultItem,
  GenericItem,
  GetToggleButtonProps,
  ItemsOrGroupedItems,
  UseMultiDropdownProps,
} from '../types';

/**
 * A hook used to manage all logic needed for a multiple select Dropdown.
 */
const useMultiDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLButtonElement,
>({
  id,
  items,
  groupedItems,
  selectedItems: selectedItemsProp,
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  isItemDisabled = defaultIsItemDisabled,
  target: targetRefProp,
  overlayRef: overlayRefProp,
  onStateChange: onStateChangeProp,
  onItemSelect: onItemSelectProp,
  getItemKey: getItemKeyProp,
  ...props
}: UseMultiDropdownProps<TItem, TTarget>) => {
  const dropdownId = useDropdownId(id);
  const targetRef = useForwardedRef(targetRefProp ?? null);
  const overlayRef = useForwardedRef(overlayRefProp ?? null);
  const maybeGroupedItems = useMemo(
    () => items ?? groupedItems ?? [],
    [groupedItems, items],
  );

  // Create default getItemKey if not provided
  const getItemKey = useMemo(
    () => getItemKeyProp || createDefaultGetItemKey(itemToString),
    [getItemKeyProp, itemToString],
  );

  checkReferenceEquality({
    ...props,
    items: maybeGroupedItems,
    selectedItems: selectedItemsProp,
  });

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }

  const {
    getDropdownProps,
    getSelectedItemProps: rawGetSelectedItemProps,
    selectedItems: rawSelectedItems,
    addSelectedItem: rawAddSelectedItem,
    removeSelectedItem: rawRemoveSelectedItem,
    ...useMultipleSelectionReturnValue
  } = useMultipleSelection<TItem>({
    ...useMultipleSelectionArgs,
    ...props,
  });

  const selectedItems = useMemo(
    () =>
      getItemKeyProp
        ? deduplicateItems(rawSelectedItems, getItemKey)
        : rawSelectedItems,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getItemKeyProp only gates the branch; getItemKey already changes with it
    [rawSelectedItems, getItemKey],
  );

  const addSelectedItem = useCallback(
    (item: TItem) => {
      if (!includesItem(rawSelectedItems, item, getItemKey)) {
        rawAddSelectedItem(item);
      }
    },
    [rawAddSelectedItem, rawSelectedItems, getItemKey],
  );

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const itemWasRemoved = useRef(false);

  const removeSelectedItem = useCallback(
    (item: TItem) => {
      itemWasRemoved.current = true;

      rawRemoveSelectedItem(item);
    },
    [rawRemoveSelectedItem],
  );

  const stateReducer = useCallback(
    (
      state: UseSelectState<TItem>,
      { changes, type }: UseSelectStateChangeOptions<TItem>,
    ): Partial<UseSelectState<TItem>> => {
      if (type !== useSelect.stateChangeTypes.ToggleButtonBlur) {
        itemWasRemoved.current = false;
      }

      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonBlur: {
          let isOpen = getShouldKeepMenuOpenOnBlur();

          // NOTE(madeira): If the last operation was deselection, we want to force open state
          if (itemWasRemoved.current) {
            isOpen = true;
          }

          return {
            ...changes,
            isOpen,
          };
        }
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection
            highlightedIndex: state.highlightedIndex, // maintain highlighted item after selection
          };
        default:
          return changes;
      }
    },
    [getShouldKeepMenuOpenOnBlur],
  );

  const onStateChange = useCallback(
    ({ type, selectedItem }: UseSelectStateChange<TItem>) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (
            selectedItem !== null &&
            selectedItem !== undefined &&
            !isGroupHeader(selectedItem)
          ) {
            handleMultiItemSelect({
              item: selectedItem,
              selectedItems,
              addSelectedItem,
              removeSelectedItem,
              getItemKey,
              onItemSelect: onItemSelectProp,
            });
          }
          break;
        default:
          break;
      }

      onStateChangeProp?.({ type, selectedItem });
    },
    [
      addSelectedItem,
      onItemSelectProp,
      onStateChangeProp,
      removeSelectedItem,
      selectedItems,
      getItemKey,
    ],
  );

  // workaround, the implicit XOR type gets lost causing type errors.  Casting it here makes it explicit
  const itemArgs = { items, groupedItems } as ItemsOrGroupedItems<TItem>;

  const {
    controlProps: {
      ref,
      isOpen,
      selectedItem: _ignored1,
      getToggleButtonProps: rawGetToggleButtonProps,
      ...controlProps
    },
    menuProps: {
      selectedItem: _ignored2,
      getItemProps: rawGetItemProps,
      ...menuProps
    },
    ...useDropdownReturnValue
  } = useDropdown({
    id: dropdownId,
    ...itemArgs,
    selectedItem: null,
    itemToString,
    stateReducer,
    onStateChange,
    isItemDisabled,
    target: targetRef,
    overlayRef,
    getItemKey,
    ...props,
  });

  const getToggleButtonProps = useCallback<GetToggleButtonProps>(
    ({ onKeyDown: onKeyDownProp, ref: optionsRef, ...options } = {}) => {
      const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      return rawGetToggleButtonProps({
        ...getDropdownProps({
          ref: (optionsRef ?? ref) as unknown as RefObject<HTMLButtonElement>,
          ...options,
          preventKeyAction: isOpen,
        }),
        onKeyDown,
      });
    },
    [controlOnKeyDown, getDropdownProps, isOpen, rawGetToggleButtonProps, ref],
  );

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const _isItemDisabled = useCallback(
    (item: TItem) => isItemDisabled(item, flatItems.indexOf(item)),
    [flatItems, isItemDisabled],
  );

  const multipleSelectionHandlers = useMemo(
    () =>
      getMultipleSelectionHandlers({
        id: dropdownId,
        selectedItems,
        setSelectedItems: useMultipleSelectionReturnValue.setSelectedItems,
        isItemDisabled: _isItemDisabled,
        closeMenu: useDropdownReturnValue.closeMenu,
        rawGetItemProps,
        rawGetSelectedItemProps,
        refToFocusOnClear: targetRef,
        getItemKey,
      }),
    [
      dropdownId,
      _isItemDisabled,
      rawGetItemProps,
      rawGetSelectedItemProps,
      selectedItems,
      targetRef,
      useDropdownReturnValue.closeMenu,
      useMultipleSelectionReturnValue.setSelectedItems,
      getItemKey,
    ],
  );

  return {
    getSelectedItemProps:
      multipleSelectionHandlers.controlProps.getSelectedItemProps,
    controlProps: {
      ref,
      ...controlProps,
      isOpen,
      getToggleButtonProps,
      selectedItems,
      removeSelectedItem,
      ...multipleSelectionHandlers.controlProps,
    },
    menuProps: {
      ...menuProps,
      selectedItems,
      addSelectedItem,
      removeSelectedItem,
      ...multipleSelectionHandlers.menuProps,
    },
    ...useMultipleSelectionReturnValue,
    ...useDropdownReturnValue,
  };
};

export default Object.assign(useMultiDropdown, {
  stateChangeTypes: useDropdown.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
